import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { resolveURLPath } from 'utils/urlHelpers';


const BUFFER_TIME = 10;

class CoreService {
  #baseUrl = undefined;
  #fetchInProgress = null;
  #accessToken = {
    token: undefined,
    expiryTimestamp: 0,
  };

  #user = {};

  #fetchToken = async() => {
    const baseUrl = ConfigurationService.get(ConfigurationKeys.BASE_URL);
    const endPoint = 'rest/v1.0/partner/accounts/info';

    const url = new URL(baseUrl);
    url.pathname = resolveURLPath(url.pathname, endPoint);

    try {
      const { data } = await ApiService.get(url.toString());
      const { accessTokenDetails, userInfo } = data;
      const { token, ttlInHours, modelId, role } = accessTokenDetails;

      if (!token || !ttlInHours) {
        throw new Error('No token or invalid details for connecting to FanClub BE');
      }

      const expiryDate = new Date();
      expiryDate.setHours(expiryDate.getHours() + ttlInHours);
      const expiryTimestamp = expiryDate.getTime();

      this.#accessToken = { token, expiryTimestamp };

      const { canBeLinked = true, roleCanBeChanged = true } = userInfo || {};
      const isPerformer = role === 'MODEL';

      this.#user = {
        id: modelId,
        role,
        isPerformer,
        canActivate: !isPerformer && canBeLinked && roleCanBeChanged,
      };
    } catch (err) {
      this.#accessToken = { token: undefined, expiryTimestamp: 0 };
      this.#user = {};
    }

    this.#fetchInProgress = null;
  };

  resetToken = () => {
    this.#baseUrl = undefined;
    this.#accessToken = {
      token: undefined,
      expiryTimestamp: 0,
    };

    this.#fetchInProgress = null;

    this.#user = {};
  };

  getToken = async(options = {}) => {
    const { forceRefresh = false } = options;
    const { token, expiryTimestamp } = this.#accessToken;

    const date = new Date();
    const hasExpired = date.getTime() > expiryTimestamp;

    date.setMinutes(date.getMinutes() + BUFFER_TIME);
    const expiresSoon = date.getTime() > expiryTimestamp;

    if (!forceRefresh && token && !hasExpired) {
      if (expiresSoon && !this.#fetchInProgress) {
        this.#fetchInProgress = this.#fetchToken();
      }

      return { ...this.#accessToken };
    }

    if (forceRefresh || !this.#fetchInProgress) {
      this.#fetchInProgress = this.#fetchToken();
    }

    await this.#fetchInProgress;

    return { ...this.#accessToken };
  };

  getUser = async() => {
    await this.getToken();

    return { ...this.#user };
  };

  get baseUrl() {
    if (!this.#baseUrl) {
      this.#baseUrl = ConfigurationService.get(ConfigurationKeys.TEMPTED_BASE_URL);
    }

    return this.#baseUrl;
  }

  computeURL = (endPoint, prefix = 'api/rest/v1/tenants') => {
    const url = new URL(this.baseUrl);

    if (prefix) {
      url.pathname = resolveURLPath(url.pathname, prefix);
    }

    if (endPoint) {
      url.pathname = resolveURLPath(url.pathname, endPoint);
    }

    return url;
  };
}

export default new CoreService();
